/**
 * @fileoverview Tree expansion management hook for workspace editor components
 *
 * This hook provides tree expansion/collapse functionality for workspace editor
 * components with different behaviors for assembly-type projects vs regular projects.
 * It handles expand all, collapse all operations, tracks expanded keys, and manages
 * product family fetching with error handling and user notifications.
 *
 * @author Claude Code
 * @version 1.0
 */

import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { Key, useCallback, useMemo } from 'react'
import type { DataNode } from '../types'
import { PartType } from '../types'
import * as utils from '../utils'
import { useWorkspace2Actions } from '../Workspace2Store'

/**
 * Return type for the useTreeExpansion hook
 */
interface TreeExpansionHandlers {
    /** Handler function to expand all tree nodes */
    handleExpandAll: () => void
    /** Handler function to collapse all tree nodes */
    handleCollapseAll: () => void
    /** Array of keys that can be expanded (not currently expanded) */
    keysToExpand: string[]
}

/**
 * Permission configuration for tree expansion behavior
 */
interface Permission {
    /** Whether the current project is an assembly-type project */
    isAssemblyTypeProject: boolean
}

/**
 * Function type for fetching product family data
 */
interface FetchProductFamilyFn {
    /**
     * Fetches product family data for specified part type and content item IDs
     * @param partType - The type of part to fetch
     * @param rootArticleContentItemIds - Optional array of content item IDs to fetch
     */
    (partType: PartType, rootArticleContentItemIds?: string[]): void
}

/**
 * Custom hook for managing tree expansion/collapse functionality in workspace editor
 *
 * This hook provides different expansion behaviors based on project type:
 * - Assembly projects: Uses classification node keys for expansion
 * - Regular projects: Expands content items and fetches product families
 *
 * @param permission - Configuration object containing project type information
 * @param memoContentItemViewIds - Array of memoized content item view IDs
 * @param classificationNodeKeys - Optional array of classification node keys for assembly projects
 * @param expandedKeys - Array of currently expanded keys
 * @param setArticlesLoading - Function to set loading state for articles
 * @param fetchProductFamily - Function to fetch product family data
 * @param activeTab - Current active tab number
 * @param treeData - Tree data structure containing nodes
 * @param withKeysToFetch - Function to determine which keys need to be fetched
 * @param withKeysToCollapse - Function to determine which keys to collapse
 *
 * @returns TreeExpansionHandlers object containing expansion handlers and keys
 */
export const useTreeExpansion = (
    permission: Permission,
    memoContentItemViewIds: string[],
    classificationNodeKeys: string[] | undefined,
    expandedKeys: string[],
    setArticlesLoading: (keys: string[]) => void,
    fetchProductFamily: FetchProductFamilyFn,
    activeTab: number,
    treeData: DataNode[],
    withKeysToFetch: (keysToExpand: string[]) => string[],
    withKeysToCollapse: (treeNodes: DataNode[]) => Key[]
): TreeExpansionHandlers => {
    const { setExpandedKeys } = useWorkspace2Actions()
    const notification = useNotification()

    const onExpandAllAssemblies = useCallback(() => {
        if (classificationNodeKeys) {
            setExpandedKeys(classificationNodeKeys)
        }
    }, [classificationNodeKeys, setExpandedKeys])

    const onCollapseAllAssemblies = useCallback(() => setExpandedKeys([]), [
        setExpandedKeys,
    ])

    const onExpandAll = useCallback(() => {
        try {
            const keysToExpand = memoContentItemViewIds.filter(
                (key) => !expandedKeys.includes(key)
            )

            const articleKeysToFetch = withKeysToFetch(keysToExpand)

            if (articleKeysToFetch.length > 0) {
                setArticlesLoading(articleKeysToFetch)

                if (fetchProductFamily) {
                    fetchProductFamily(
                        utils.getActiveTabLabel(activeTab),
                        articleKeysToFetch
                    )
                }
            }

            setExpandedKeys([...expandedKeys, ...keysToExpand])
        } catch (error) {
            console.error('Error expanding all items:', error)
            // Add user notification or error callback
            notification.error({
                message:
                    error instanceof Error
                        ? error.message
                        : 'Error expanding all items',
            })
        }
    }, [
        memoContentItemViewIds,
        expandedKeys,
        withKeysToFetch,
        setArticlesLoading,
        fetchProductFamily,
        activeTab,
        setExpandedKeys,
    ])

    const onCollapseAll = useCallback(() => {
        try {
            setExpandedKeys(withKeysToCollapse(treeData))
            setArticlesLoading([])
        } catch (error) {
            console.error('Error collapsing all items:', error)
            // Add user notification or error callback
            notification.error({
                message:
                    error instanceof Error
                        ? error.message
                        : 'Error collapsing all items',
            })
        }
    }, [treeData, withKeysToCollapse, setExpandedKeys, setArticlesLoading])

    const handleExpandAll = useCallback(() => {
        if (
            !memoContentItemViewIds?.length &&
            !classificationNodeKeys?.length
        ) {
            notification.warning({ message: 'No content items to expand' })
            return
        }
        permission.isAssemblyTypeProject
            ? onExpandAllAssemblies()
            : onExpandAll()
    }, [onExpandAllAssemblies, onExpandAll])

    const handleCollapseAll = useCallback(
        () =>
            permission.isAssemblyTypeProject
                ? onCollapseAllAssemblies()
                : onCollapseAll(),
        [
            permission.isAssemblyTypeProject,
            onCollapseAllAssemblies,
            onCollapseAll,
        ]
    )
    const getKeysToExpand = (currentExpanded: string[], allKeys?: string[]) =>
        allKeys?.filter((key) => !currentExpanded.includes(key))
    const keysToExpand = useMemo(() => {
        const sourceKeys = permission.isAssemblyTypeProject
            ? classificationNodeKeys
            : memoContentItemViewIds

        return getKeysToExpand(expandedKeys, sourceKeys) ?? []
    }, [
        permission.isAssemblyTypeProject,
        classificationNodeKeys,
        memoContentItemViewIds,
        expandedKeys,
    ])

    return { handleExpandAll, handleCollapseAll, keysToExpand }
}
